import {
    ProcessCellForExportParams,
    ValueFormatterParams,
    ColDef,
    ValueGetterParams,
    KeyCreatorParams,
} from 'ag-grid-community';
import { t } from 'i18next';
import {
    ORG_CHART_HR_MAPPING,
    ORG_CHART_HR_NAME,
    ORG_CHART_HR_SOFTWARE_ID,
} from 'routes/HumanResourcesModule/business/definitions';
import { TEXT_FILTER } from './defaultColDefs';

/**
 * Formats the orgChartHrName with its mapping.
 * @param orgChartHrName - The original name.
 * @param orgChartHrMapping - The mapping for the name.
 * @returns The formatted string combining the name and its mapping.
 */
export const orgChartHrFormatter = ({
    orgChartHrName,
    orgChartHrMapping,
}: {
    orgChartHrName: string;
    orgChartHrMapping: string | undefined;
}) =>
    orgChartHrMapping
        ? `${orgChartHrName} (${orgChartHrMapping})`
        : orgChartHrName;

/**
 * Formats the value for orgChartHr column. It handles different cases based on whether the node is a group
 * and the type of value provided.
 * @param params - Parameters that include the value to format and node information.
 * @returns The formatted value as a string.
 */
export const orgChartHrValueFormatter = (
    params: ProcessCellForExportParams | ValueFormatterParams,
) => {
    const orgChartHrMapping = params?.value?.[ORG_CHART_HR_MAPPING] ?? '';
    const orgChartHrName = params?.value?.[ORG_CHART_HR_NAME] ?? '';
    // handle hypothesis linked to no services
    if (params?.node?.key === '..unmapped') {
        return t('humanResources.obsoleteService');
    }
    // If the node is a group, we assume it's the result from keyCreator
    if (params?.node?.group) {
        return params.node.key?.includes('..')
            ? orgChartHrName
            : orgChartHrMapping;
    }

    // Sinon, utilisez ORG_CHART_HR_MAPPING ou ORG_CHART_HR_NAME
    return orgChartHrFormatter({ orgChartHrMapping, orgChartHrName });
};

type GroupedOrgChartValue = {
    [ORG_CHART_HR_MAPPING]?: string;
    [ORG_CHART_HR_NAME]?: string;
    [ORG_CHART_HR_SOFTWARE_ID]?: string;
};

/**
 * Creates a column definition for the orgChartHR, handling grouped values and mapping.
 * @param rowGroup - Indicates if the column should be used for row grouping.
 * @returns The column definition object.
 */
export const getGroupedOrgChartHrColumn = (
    rowGroup: boolean = true,
): ColDef => ({
    ...TEXT_FILTER,
    headerName: t('humanResources.payState.orgChartHrs'),
    rowGroup,
    field: ORG_CHART_HR_MAPPING,
    enableRowGroup: true,
    hide: rowGroup,
    valueGetter: (params: ValueGetterParams): GroupedOrgChartValue => ({
        [ORG_CHART_HR_MAPPING]: params?.data?.[ORG_CHART_HR_MAPPING],
        [ORG_CHART_HR_NAME]: params?.data?.[ORG_CHART_HR_NAME],
        [ORG_CHART_HR_SOFTWARE_ID]: params?.data?.[ORG_CHART_HR_SOFTWARE_ID],
    }),
    filterValueGetter: (params: ValueGetterParams) =>
        orgChartHrFormatter({
            orgChartHrMapping: params?.data?.[ORG_CHART_HR_MAPPING] || '',
            orgChartHrName: params?.data?.[ORG_CHART_HR_NAME] || '',
        }),
    keyCreator: (params: KeyCreatorParams) => {
        return (
            params.value[ORG_CHART_HR_MAPPING] ||
            `${params.value[ORG_CHART_HR_NAME]}..${params.value[ORG_CHART_HR_SOFTWARE_ID]}`
        );
    },
    valueFormatter: orgChartHrValueFormatter,
    comparator: (
        valueA: {
            [ORG_CHART_HR_NAME]?: string;
            [ORG_CHART_HR_MAPPING]?: string;
        },
        valueB: {
            [ORG_CHART_HR_NAME]?: string;
            [ORG_CHART_HR_MAPPING]?: string;
        },
    ) => {
        const {
            [ORG_CHART_HR_MAPPING]: mappingA = '',
            [ORG_CHART_HR_NAME]: nameA = '',
        } = valueA;
        const {
            [ORG_CHART_HR_MAPPING]: mappingB = '',
            [ORG_CHART_HR_NAME]: nameB = '',
        } = valueB;
        const valA = mappingA || nameA;
        const valB = mappingB || nameB;
        let result = 0;
        if (valA > valB) {
            result = 1;
        } else if (valA < valB) {
            result = -1;
        }
        return result;
    },
});
